import {
  BottomSheet,
  Text,
  TextArea,
  Title,
  ToggleButton,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { DialogContent, Stack } from "@mui/material";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { isSameMinute } from "date-fns";

import { areTimeDisplaysIdenticalAcrossTimezones } from "../../../utils/areTimeDisplaysIdenticalAcrossTimezones";
import { formatTimeWithTimezone } from "../../../utils/formatTimeWithTimezone";
import { getTimezoneAbbreviation } from "../../../utils/getTimezoneAbbreviation";

interface InterviewUpdateEtaBottomSheetProps {
  modalState: UseModalState;
  interviewStart: Date;
  workplaceTimezone: string;
  workerTimezone: string;
  etaAvailableTimes: Date[];
  selectedEta: Date;
  message: string;
  additionalMessage: string;
  isSubmitting: boolean;
  onEtaChange: (eta: Date) => void;
  onMessageChange: (message: string) => void;
  onSubmit: () => Promise<void>;
}

export function InterviewUpdateEtaBottomSheet(props: InterviewUpdateEtaBottomSheetProps) {
  const {
    modalState,
    interviewStart,
    workplaceTimezone,
    workerTimezone,
    etaAvailableTimes,
    selectedEta,
    message,
    additionalMessage,
    isSubmitting,
    onEtaChange,
    onMessageChange,
    onSubmit,
  } = props;

  const showTimezoneAbbreviation = !areTimeDisplaysIdenticalAcrossTimezones(
    interviewStart,
    workplaceTimezone,
    workerTimezone
  );

  return (
    <BottomSheet
      modalState={modalState}
      header={<BottomSheetHeader onClose={modalState.closeModal} />}
      footer={
        <DialogFooter orientation="vertical">
          <LoadingButton
            fullWidth
            isLoading={isSubmitting}
            size="large"
            variant="contained"
            endIconType="plane"
            onClick={onSubmit}
          >
            Send message
          </LoadingButton>
        </DialogFooter>
      }
    >
      <DialogContent sx={{ textAlign: "left" }}>
        <Stack direction="column" spacing={8} px={3}>
          <Stack spacing={4}>
            <Title semibold variant="h3" component="h3">
              When will you arrive?
            </Title>
            <Text variant="body2">Let the workplace know when you will arrive.</Text>
          </Stack>

          <Stack spacing={6}>
            <Text semibold>
              {showTimezoneAbbreviation
                ? `I will arrive at (${getTimezoneAbbreviation(workplaceTimezone)})`
                : "I will arrive at"}
            </Text>
            <Stack direction="row" flexWrap="wrap" sx={{ gap: 4 }}>
              {etaAvailableTimes.map((time) => {
                const isInterviewStartTime = isSameMinute(time, interviewStart);
                const formattedTime = formatTimeWithTimezone(time, workplaceTimezone, false);
                return (
                  <ToggleButton
                    key={time.toISOString()}
                    value={time}
                    sx={{ minWidth: "auto", px: 2, flex: "1 0 30%" }}
                    selected={selectedEta.toISOString() === time.toISOString()}
                    onChange={(_, value: Date | null) => {
                      if (value) {
                        onEtaChange(value);
                      }
                    }}
                  >
                    {isInterviewStartTime ? `on time, at ${formattedTime}` : formattedTime}
                  </ToggleButton>
                );
              })}
            </Stack>
          </Stack>

          <Stack spacing={4}>
            <Text semibold>Your message</Text>
            <Text sx={{ fontStyle: "italic" }}>{message}</Text>
          </Stack>

          <Stack spacing={6}>
            <Text semibold>Additional message (optional)</Text>
            <TextArea
              fullWidth
              rows={3}
              placeholder="Provide additional details"
              value={additionalMessage}
              onChange={(event) => {
                onMessageChange(event.target.value);
              }}
            />
          </Stack>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
